import {
  OreButton,
  OreCheckbox,
  OreHeading,
  OreMessageBar,
  OreModal,
  OreModalBox,
  OreModalBoxBody,
  OreModalBoxFooter,
  OreStack,
  OreText,
  OreTextArea,
} from '@edenredespana/oreneta'
import { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'

import { LegalDocumentType } from '@/Shared/domain'
import { ModalActions } from '@/Shared/ui/Modal/atoms'
import { useModal } from '@/Shared/ui/Modal/hooks'
import { useDownloaderHook } from '@/Shared/ui/downloader'
import { handleErrors } from '@/Shared/ui/form'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { DeleteAccountDataForm } from '@/User/domain'
import { createApiUserRepository } from '@/User/infrastructure'
import { useDeleteAccount } from '@/User/ui/hooks/useDeleteAccount'

import { deleteAccountTranslation } from '../userTranslation'

interface IProps {
  ref: React.Ref<ModalActions>
}

export const DeleteAccountModal = forwardRef<ModalActions, IProps>(
  (_, ref): JSX.Element => {
    const { t } = useTranslation()
    const { downloadLegalDocument } = useDownloaderHook()
    const {
      deleteAccount,
      errorMessage,
      form: { formState, register, handleSubmit, reset },
    } = useDeleteAccount(createApiUserRepository())
    const { errors } = handleErrors(formState)
    const { close, isOpen } = useModal(ref)

    const saveAndClose = async (data: DeleteAccountDataForm): Promise<void> => {
      const result = await deleteAccount(data)

      if (result) {
        close()
      }
    }

    if (!isOpen) return <></>

    return (
      <OreModal open={isOpen}>
        <OreModalBox
          size="medium"
          handleOnClose={(): void => {
            reset()
            close()
          }}
        >
          <OreModalBoxBody>
            <OreHeading size="headline-lg">
              {t(deleteAccountTranslation.modal.title)}
            </OreHeading>
            <OreText>{t(deleteAccountTranslation.modal.description)}</OreText>
            <form>
              <OreStack placeContent="stretch" space="medium">
                <OreTextArea
                  {...register('reason')}
                  {...errors('reason')}
                  name="reason"
                  id="reason"
                  label={t(deleteAccountTranslation.modal.reason)}
                  placeholder={t(deleteAccountTranslation.modal.reasonPlaceholder)}
                  rows={3}
                  maxLength={401}
                  required
                />

                {errorMessage ? (
                  <OreMessageBar color="error">{errorMessage}</OreMessageBar>
                ) : null}
                <OreCheckbox
                  {...register('acceptConditions')}
                  label={
                    <>
                      {t(deleteAccountTranslation.modal.acceptConditions.label)}
                      <button
                        type="button"
                        onClick={async (): Promise<void> =>
                          downloadLegalDocument(
                            LegalDocumentType.cancellationTerms,
                            t(deleteAccountTranslation.modal.acceptConditions.filename),
                          )
                        }
                        style={{ textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {t(deleteAccountTranslation.modal.acceptConditions.link)}
                      </button>
                      {t(deleteAccountTranslation.modal.acceptConditions.label2)}
                    </>
                  }
                  {...errors('acceptConditions')}
                  required
                />
                <OreText>{t(formTranslation.common.requiredFields)}</OreText>
              </OreStack>
            </form>
          </OreModalBoxBody>
          <OreModalBoxFooter>
            <OreButton
              onClick={(): void => {
                reset()
                close()
              }}
              size="small"
              category="secondary"
            >
              {t(formTranslation.common.buttons.cancel)}
            </OreButton>
            <OreButton
              onClick={handleSubmit(saveAndClose)}
              size="small"
              category="danger"
              data-testid="confirmDelete"
            >
              {t(formTranslation.common.buttons.delete)}
            </OreButton>
          </OreModalBoxFooter>
        </OreModalBox>
      </OreModal>
    )
  },
)
